import { formatShowTime, parseWallClock } from "../../app/showNight.js";

/** Formatting shared by the read-only shared-show views and the guest editor (ART-165). Split out of
 *  `sharedShowSections.tsx` so that file exports only components (react-refresh). */

/** The lineup slot vocabulary (`show_lineup_slots.slot_role`), in billing order. The one list of these words: the
 *  show editor and Send to team offer it, and every surface that SHOWS a slot reads `slotRoleLabel` (ART-354). */
export const SLOT_ROLES: { value: string; label: string }[] = [
  { value: "headliner", label: "Headliner" },
  { value: "co_headliner", label: "Co-headliner" },
  { value: "direct_support", label: "Direct Support" },
  { value: "support", label: "Support" },
  { value: "opener", label: "Opener" },
  { value: "local_support", label: "Local Support" },
];

const SLOT_ROLE_LABELS: Record<string, string> = Object.fromEntries(SLOT_ROLES.map((r) => [r.value, r.label]));

/** "direct_support" → "Direct Support". A key outside the list is title-cased, never printed raw. */
export function slotRoleLabel(role: string | null | undefined): string | null {
  const key = role?.trim();
  if (!key) return null;
  return SLOT_ROLE_LABELS[key]
    ?? key.split(/[_\s]+/).filter(Boolean).map((w) => w[0]!.toUpperCase() + w.slice(1)).join(" ");
}

const MONTHS = ["Jan", "Feb", "Mar", "Apr", "May", "Jun", "Jul", "Aug", "Sep", "Oct", "Nov", "Dec"];

/** YYYY-MM-DD wall-clock → "Aug 8, 2026", no timezone shift (the client never tz-converts a show date). */
export function sharedShowFormatDate(date: string | undefined | null): string {
  if (!date) return "";
  const m = /^(\d{4})-(\d{2})-(\d{2})/.exec(date);
  if (!m) return date;
  const [, y, mo, d] = m;
  return `${MONTHS[Number(mo) - 1] ?? mo} ${Number(d)}, ${y}`;
}

const RUN_OF_SHOW_SECTION_LABELS: Record<string, string> = {
  load_in: "Load in",
  soundcheck: "Soundcheck",
  doors: "Doors",
  stage: "Set",
  load_out: "Load out",
  curfew: "Curfew",
};

/** A run-of-show row's name: its own label, else its section's label. `section` is a vocabulary key
 *  ("load_in"), so an unmapped one is sentence-cased rather than printed raw. */
export function sharedRunOfShowLabel(item: { label?: string | null; section?: string | null }): string {
  const own = item.label?.trim();
  if (own) return own;
  const key = item.section ?? "";
  const words = RUN_OF_SHOW_SECTION_LABELS[key] ?? key.replace(/_/g, " ").trim();
  return words ? words[0].toUpperCase() + words.slice(1) : "Item";
}

/** Stored wall clock → the day-sheet reading (the SHARED pair, so `21:00` and `9pm` never disagree). */
export function sharedShowFormatTime(t: string | undefined | null): string {
  return formatShowTime(parseWallClock(t));
}
